/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import { type ForwardedRef, type ReactNode, createContext, forwardRef, useRef } from 'react';
import { useFocusRing, useHover, usePress } from 'react-aria';
import {
  type ContextValue,
  InputContext as InputContextCore,
  Input as InputCore,
  type InputProps as InputCoreProps,
  Provider,
  useContextProps,
  useRenderProps
} from 'react-aria-components';
import { $ } from '../../tokens.stylex';
import { IconButtonContext } from '../icon-button/icon-button.component';
import { IconContext } from '../icon/icon.component';
import { Label, LabelContext } from "../label/label.component";

interface LabeledInputStyleProps {
  /** The variant of the input. (Default: outlined) */
  variant?: keyof typeof variantVariants;
  /** The label of the input. */
  label?: string;
  /** Used to add accessories to the input. */
  children?: ((values: {
    isHovered: boolean;
    isFocused: boolean;
    isFocusVisible: boolean;
    isDisabled: boolean;
    isInvalid: boolean;
    defaultChildren: ReactNode | undefined;
  }) => ReactNode);
  style?: stylex.StyleXStyles;
}

type LabeledInputProps = Omit<InputCoreProps, 'style' | 'className' | 'children'> & LabeledInputStyleProps;

const LabeledInputStyleContext = createContext<ContextValue<LabeledInputStyleProps, HTMLDivElement>>({});

const LabeledInput = forwardRef((props: LabeledInputProps, ref: ForwardedRef<HTMLDivElement>) => {
  let inputRef = useRef<HTMLInputElement>(null);
  let contextProps: InputCoreProps = {};

  [props, ref] = useContextProps(props, ref, LabeledInputStyleContext);
  [contextProps, inputRef] = useContextProps({}, inputRef, InputContextCore);

  const { style, variant = 'outlined', label, placeholder, children, ...coreProps } = props;

  const { hoverProps, isHovered } = useHover(coreProps);
  const { isFocused, isFocusVisible, focusProps } = useFocusRing({
    isTextInput: true,
    autoFocus: coreProps.autoFocus,
    within: true,
  });
  // onKeyDown prevents spaces from being entered into the input field
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { pressProps: { onKeyDown, ...pressProps } } = usePress({
    onPressStart: () => inputRef.current?.focus(),
    preventFocusOnPress: true,
  });

  const isInvalid = (!!coreProps['aria-invalid'] && coreProps['aria-invalid'] !== 'false') || !!contextProps['aria-invalid'];
  const isDisabled = coreProps.disabled || contextProps.disabled || false;
  const hasValue = !!coreProps.value || !!inputRef.current?.value?.length || !!contextProps.value;

  const renderProps = useRenderProps({
    ...coreProps,
    values: {
      isHovered,
      isFocused,
      isFocusVisible,
      isDisabled,
      isInvalid
    },
    defaultChildren: (
      <div {...stylex.props(styles.contentContainer)}>
        <Label />
        <InputCore
          ref={inputRef}
          placeholder={isFocused || hasValue ? placeholder : undefined}
          {...stylex.props(inputStyles.base)}
          {...coreProps}
        />
      </div>),
    children: (renderProps) => (
      <Provider values={[
        [LabelContext, {
          size: isFocused || contextProps['aria-expanded'] || hasValue ? 12 : 16,
          color: (
            (isDisabled && 'disabled') ||
            (isInvalid && 'critical') ||
            ((isFocused || contextProps['aria-expanded']) && 'interactive') ||
            (hasValue && 'tertiary') ||
            (isHovered && 'quaternary') ||
            'placeholder'
          ),
          style: [labelStyles.base, (isFocused || contextProps['aria-expanded'] || hasValue) && labelStyles.floating],
          children: label
        }],
        [IconButtonContext, {
          variant: 'ghost',
          size: 32,
          isDisabled,
        }],
        [IconContext, {
          color: (isDisabled && 'disabled') || 'tertiary'
        }]]}>
        {children?.(renderProps) || renderProps.defaultChildren}
      </Provider>
    )
  });

  return (
    <div
      ref={ref}
      data-focused={isFocused || contextProps['aria-expanded'] || undefined}
      data-disabled={isDisabled || undefined}
      data-hovered={isHovered || undefined}
      data-focus-visible={isFocusVisible || undefined}
      data-invalid={isInvalid || undefined}
      {...pressProps}
      {...hoverProps}
      {...focusProps}
      {...renderProps}
      {...stylex.props(styles.base, variantVariants[variant], style)}>
      {renderProps.children}
    </div>
  );
});

const styles = stylex.create({
  base: {
    alignItems: 'center',
    borderRadius: 10,
    borderStyle: 'solid',
    boxSizing: 'border-box',
    columnGap: 12,
    cursor: {
      default: 'text',
      ':is([data-disabled])': 'not-allowed',
    },
    display: 'flex',
    height: 52,
    minWidth: 0,
    overflow: 'hidden',
    paddingBottom: {
      default: 6,
      ':is([data-focused], [data-focus-within], [data-invalid])': 5,
    },
    paddingLeft: {
      default: 14,
      ':is([data-focused], [data-focus-within], [data-invalid])': 13,
    },
    paddingRight: {
      default: 14,
      ':is([data-focused], [data-focus-within], [data-invalid])': 13,
    },
    paddingTop: {
      default: 6,
      ':is([data-focused], [data-focus-within], [data-invalid])': 5,
    },
    position: 'relative',
  },
  contentContainer: {
    cursor: 'inherit',
    display: 'flex',
    flex: '1 1 0',
    height: '100%',
    minWidth: 0,
    position: 'relative',
  }
});

const inputStyles = stylex.create({
  base: {
    backgroundColor: 'transparent',
    borderStyle: 'none',
    caretColor: $.textInteractive,
    color: {
      default: $.textPrimary,
      ':is([data-disabled])': $.textDisabled,
    },
    cursor: 'inherit',
    fontFamily: 'BasierCircle',
    fontFeatureSettings: 'liga off, clig off',
    fontSize: 16,
    fontStyle: 'normal',
    fontWeight: 400,
    letterSpacing: -0.32,
    outline: 'none',
    overflow: 'hidden',
    paddingBottom: 0,
    paddingLeft: 0,
    paddingRight: 0,
    paddingTop: 15,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',
    '::placeholder': { color: $.textPlaceholder },
    '::selection': { backgroundColor: $.surfaceHighlightMedium },
  }
});

const labelStyles = stylex.create({
  base: {
    backgroundColor: 'transparent',
    cursor: 'inherit',
    fontFamily: 'BasierCircle',
    fontFeatureSettings: 'liga off, clig off',
    fontWeight: 400,
    position: 'absolute',
    top: '50%',
    transform: 'translate(0, -50%)',
    transition: 'font-size 0.15s, top 0.1s ease-in-out',
    whiteSpace: 'nowrap',
  },
  floating: {
    fontWeight: 500,
    top: '23%',
  }
});

const variantVariants = stylex.create({
  outlined: {
    backgroundColor: $.surfaceBGPrimary,
    borderColor: {
      default: $.border16,
      ':is([data-hovered]):not([data-focused], [data-focus-within], [data-invalid], [data-disabled])': $.border24,
      ':is([data-focused], [data-focus-within]):not([data-invalid])': $.borderInteractive,
      ':is([data-invalid])': $.borderCritical,
      ':is([data-disabled])': $.border10,
    },
    borderWidth: {
      default: 1,
      ':is([data-focused], [data-focus-within], [data-invalid])': 2,
      ':is([data-disabled])': 1,
    },
  },
  filled: {
    backgroundColor: {
      default: $.opacity06,
      ':is([data-focused], [data-focus-within]):not([data-invalid])': $.surfaceBGPrimary,
      ':is([data-invalid])': $.surfaceCriticalSilent,
    },
    borderColor: {
      default: 'transparent',
      ':is([data-focused], [data-focus-within]):not([data-invalid])': $.borderInteractive,
    },
    borderWidth: {
      default: 1,
      ':is([data-focused], [data-focus-within], [data-invalid])': 2,
    },
  },
});

export { LabeledInput, LabeledInputStyleContext, type LabeledInputProps, type LabeledInputStyleProps };
